import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";

import { useGetReleasesQuery } from "@dashboard/downloads/api";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";

export type ReleaseFilesState = {
    data: API.ReleaseFile[];
    count: number;
    status: "pending" | "fulfilled" | "rejected" | "uninitialized";
};

const defaultState: ReleaseFilesState = {
    data: [],
    count: 0,
    status: "uninitialized",
};

interface UseReleaseFilesOptions {
    pageSize?: number;
}

export const useReleaseFiles = (options: UseReleaseFilesOptions = {}): ReleaseFilesState => {
    const { pageSize = 200 } = options;

    const { data: workspace } = useCurrentWorkspace();
    const { rawWorkspaceSettings } = useAppSettingsService();
    const isTerminated = workspace?.subscription?.status === "Terminated";
    const includePublicProducts = rawWorkspaceSettings.isDownloadsPageShowingPublicProducts;
    const skip = !workspace?.id || isTerminated;

    const { data, isUninitialized, isLoading, isFetching, isError, isSuccess } = useGetReleasesQuery(
        {
            workspaceId: workspace?.id ?? 0,
            pageSize,
            includePublicProducts,
        },
        { skip },
    );

    if (!workspace?.id) {
        return defaultState;
    }

    if (isTerminated) {
        return { data: [], count: 0, status: "fulfilled" };
    }

    if (isUninitialized) {
        return defaultState;
    }

    if (isLoading || isFetching) {
        return { data: data?.list ?? [], count: data?.count ?? 0, status: "pending" };
    }

    if (isError) {
        return { data: [], count: 0, status: "rejected" };
    }

    if (isSuccess && data) {
        return { data: data.list, count: data.count, status: "fulfilled" };
    }

    return defaultState;
};
